import { useState, useCallback } from 'react';
import { Permission } from '@/types/permissions';
import { supabase } from '@/integrations/supabase/client';

export interface PermissionOverride {
  id: string;
  resource: string;
  action: string;
  name: string;
  description: string;
  granted: boolean; // true = adiciona permissão, false = remove permissão
}

export interface UsePermissionOverridesReturn {
  overrides: PermissionOverride[];
  isLoading: boolean;
  error: string | null;
  
  // Funções de gerenciamento
  addOverride: (override: PermissionOverride) => Promise<void>;
  removeOverride: (overrideId: string) => Promise<void>;
  updateOverride: (overrideId: string, updates: Partial<PermissionOverride>) => Promise<void>;
  clearAllOverrides: () => Promise<void>;
  
  // Funções de verificação
  hasOverride: (permission: string) => boolean;
  getOverride: (permission: string) => PermissionOverride | undefined;
  
  // Funções de validação
  validateOverride: (override: PermissionOverride) => string | null;
}

export const usePermissionOverrides = (userId: string): UsePermissionOverridesReturn => {
  const [overrides, setOverrides] = useState<PermissionOverride[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para carregar overrides do banco
  const loadOverrides = useCallback(async () => {
    if (!userId) return;
    
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('permission_overrides')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      const overridesData = data?.permission_overrides || [];
      setOverrides(overridesData);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar overrides';
      setError(errorMessage);
      console.error('❌ Erro ao carregar overrides:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Função para salvar overrides no banco
  const saveOverrides = useCallback(async (newOverrides: PermissionOverride[]) => {
    if (!userId) return;
    
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ permission_overrides: newOverrides })
        .eq('id', userId);

      if (updateError) throw updateError;
      
      setOverrides(newOverrides);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar overrides';
      setError(errorMessage);
      console.error('❌ Erro ao salvar overrides:', err);
      throw err;
    }
  }, [userId]);

  // Adicionar novo override
  const addOverride = useCallback(async (override: PermissionOverride) => {
    const validationError = validateOverride(override);
    if (validationError) {
      throw new Error(validationError);
    }

    const newOverrides = [...overrides, override];
    await saveOverrides(newOverrides);
  }, [overrides, saveOverrides]);

  // Remover override
  const removeOverride = useCallback(async (overrideId: string) => {
    const newOverrides = overrides.filter(o => o.id !== overrideId);
    await saveOverrides(newOverrides);
  }, [overrides, saveOverrides]);

  // Atualizar override
  const updateOverride = useCallback(async (overrideId: string, updates: Partial<PermissionOverride>) => {
    const newOverrides = overrides.map(o => 
      o.id === overrideId ? { ...o, ...updates } : o
    );
    
    // Validar override atualizado
    const updatedOverride = newOverrides.find(o => o.id === overrideId);
    if (updatedOverride) {
      const validationError = validateOverride(updatedOverride);
      if (validationError) {
        throw new Error(validationError);
      }
    }
    
    await saveOverrides(newOverrides);
  }, [overrides, saveOverrides]);

  // Limpar todos os overrides
  const clearAllOverrides = useCallback(async () => {
    await saveOverrides([]);
  }, [saveOverrides]);

  // Verificar se tem override para uma permissão
  const hasOverride = useCallback((permission: string): boolean => {
    return overrides.some(o => o.id === permission);
  }, [overrides]);

  // Obter override específico
  const getOverride = useCallback((permission: string): PermissionOverride | undefined => {
    return overrides.find(o => o.id === permission);
  }, [overrides]);

  // Validar override
  const validateOverride = useCallback((override: PermissionOverride): string | null => {
    if (!override.id || !override.resource || !override.action) {
      return 'Override deve ter id, resource e action definidos';
    }
    
    if (!override.name || !override.description) {
      return 'Override deve ter name e description definidos';
    }
    
    if (typeof override.granted !== 'boolean') {
      return 'Override deve ter granted definido como boolean';
    }
    
    // Verificar se já existe override com mesmo ID
    const existingOverride = overrides.find(o => o.id === override.id);
    if (existingOverride && existingOverride !== override) {
      return 'Já existe override com este ID';
    }
    
    return null;
  }, [overrides]);

  return {
    overrides,
    isLoading,
    error,
    addOverride,
    removeOverride,
    updateOverride,
    clearAllOverrides,
    hasOverride,
    getOverride,
    validateOverride,
  };
};
